@file:Suppress("MagicNumber", "TooManyFunctions")

package com.nonopn.skydome.common.app.pages.keraunic

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.RowScope
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.SpanStyle
import androidx.compose.ui.text.buildAnnotatedString
import androidx.compose.ui.text.withStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.nonopn.skydome.common.app.pages.pdf.KeraunicNsgLevel
import com.nonopn.skydome.common.app.pages.pdf.KeraunicReportData
import com.nonopn.skydome.common.app.pages.pdf.KeraunicReportLogic
import com.nonopn.skydome.common.app.pages.pdf.KeraunicReportText
import com.nonopn.skydome.common.app.pages.pdf.KeraunicTopImpact
import com.nonopn.skydome.common.app.pages.pdf.KeraunicZoneStats
import com.nonopn.skydome.common.app.utils.fmt

// On-screen rendering of the PDF pages, in the same order and with the same texts (PDF = reference)

internal object ReportColors {
    val Blue = Color(0xFF2B7FE0)
    val Red = Color(0xFFE63B2E)
    val Amber = Color(0xFFF5A800)
    val AmberDark = Color(0xFFD48B00)
    val Orange = Color(0xFFE65100)
    val Green = Color(0xFF2E7D32)
    val Text = Color(0xFF1A2B4A)
    val Muted = Color(0xFF6B7A99)
    val Light = Color(0xFF8A9AB5)
    val Body = Color(0xFF4A5568)
    val Surface = Color(0xFFF5F7FA)
    val SurfaceAlt = Color(0xFFF9FAFB)
    val Border = Color(0xFFE0E6F0)
    val BlueBg = Color(0xFFEEF4FF)
    val NetworkBg = Color(0xFFEEF2F8)
    val DirectBg = Color(0xFFFFF5F5)
    val IndirectBg = Color(0xFFFFFBF0)
    val KeyPointsBg = Color(0xFFFFF3E0)
    val BarTrack = Color(0xFFEEF2F8)
    val CityBar = Color(0xFFC8D6E5)
}

@Composable
internal fun ReportCard(content: @Composable () -> Unit) {
    Card(
        modifier = Modifier.fillMaxWidth(),
        colors = CardDefaults.cardColors(containerColor = Color.White),
        shape = RoundedCornerShape(12.dp),
        elevation = CardDefaults.cardElevation(defaultElevation = 2.dp)
    ) {
        Column(
            Modifier.fillMaxWidth().padding(16.dp),
            verticalArrangement = Arrangement.spacedBy(10.dp),
        ) { content() }
    }
}

// Blue uppercase section title of the PDF
@Composable
internal fun ReportSectionTitle(title: String) {
    Text(
        title.uppercase(),
        fontSize = 12.sp,
        fontWeight = FontWeight.SemiBold,
        color = ReportColors.Blue,
        letterSpacing = 1.sp,
    )
}

// Page header line of the PDF (bold subtitle)
@Composable
internal fun ReportPageHeader(title: String) {
    Text(title, fontSize = 15.sp, fontWeight = FontWeight.Bold, color = ReportColors.Text)
}

@Composable
internal fun ReportBody(text: String, color: Color = ReportColors.Body) {
    Text(text, fontSize = 13.sp, color = color, lineHeight = 19.sp)
}

internal fun polarityLabel(isPositive: Boolean) = if (isPositive) "CG+" else "CG\u2212"

internal fun polarityColor(isPositive: Boolean) = if (isPositive) ReportColors.Red else ReportColors.Blue

// ─── Page 1 — cover ────────────────────────────────────────────────────────

@Composable
internal fun KeraunicCoverSection(data: KeraunicReportData, text: KeraunicReportText, siteName: String) {
    val nsgLevel = when (data.nsgLevel) {
        KeraunicNsgLevel.HIGH -> text.cover.highActivity
        KeraunicNsgLevel.MODERATE -> text.cover.moderateActivity
        KeraunicNsgLevel.LOW -> text.cover.lowActivity
    }
    ReportCard {
        Text(text.plain(text.reportTitle), fontSize = 22.sp, fontWeight = FontWeight.ExtraBold, color = ReportColors.Text)
        Text(text.plain(text.cover.subtitle), fontSize = 13.sp, color = ReportColors.Muted)
        Column(
            Modifier.fillMaxWidth().clip(RoundedCornerShape(10.dp)).background(ReportColors.Surface).padding(14.dp),
            verticalArrangement = Arrangement.spacedBy(3.dp)
        ) {
            Text(siteName, fontSize = 17.sp, fontWeight = FontWeight.Bold, color = ReportColors.Text)
            Text(text.coordinates(data.latitude, data.longitude), fontSize = 12.sp, color = ReportColors.Muted)
            Text(
                "${text.cover.period}${text.labelColon()} ${text.period(data.periodLabel)}",
                fontSize = 12.sp,
                color = ReportColors.Muted
            )
        }
        KpiGrid(
            listOf(
                Kpi(data.totalImpacts.toString(), text.plain(text.cover.impactsDetected), ReportColors.Text),
                Kpi(data.nsg.fmt(2), text.plain(text.cover.nsg), ReportColors.Blue),
                Kpi("${data.maxKa.fmt(0)} kA", text.plain(text.cover.maximumIntensity), ReportColors.Red),
                Kpi(data.zone1.totalCount.toString(), text.plain(text.cover.directDanger), ReportColors.Text),
            )
        )
        KeraunicHighlightsBlock(data, text)
        Column(
            Modifier.fillMaxWidth().clip(RoundedCornerShape(10.dp)).background(ReportColors.BlueBg).padding(12.dp),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.spacedBy(2.dp)
        ) {
            Text(
                text.fill(text.plain(text.cover.nsgLine), data.nsg.fmt(2)),
                fontSize = 15.sp,
                fontWeight = FontWeight.Bold,
                color = ReportColors.Blue,
                textAlign = TextAlign.Center
            )
            Text("$nsgLevel ${text.cover.referencePrefix}", fontSize = 12.sp, color = ReportColors.Muted, textAlign = TextAlign.Center)
        }
    }
}

// Key facts of the cover: most intense and nearest impacts with their date and time (same lines as the PDF)
@Composable
internal fun KeraunicHighlightsBlock(data: KeraunicReportData, text: KeraunicReportText) {
    val lines = listOfNotNull(
        data.highlights.strongest?.let { text.highlights.strongestImpact to text.highlightLine(it, distanceFirst = false) },
        data.highlights.nearest?.let { text.highlights.nearestImpact to text.highlightLine(it, distanceFirst = true) },
    )
    if (lines.isEmpty() && !data.highlights.noGroundImpact) return
    Column(
        Modifier.fillMaxWidth()
            .clip(RoundedCornerShape(10.dp))
            .background(Color(0xFFFFF8E8))
            .border(1.5.dp, ReportColors.Amber, RoundedCornerShape(10.dp))
            .padding(12.dp),
        verticalArrangement = Arrangement.spacedBy(4.dp)
    ) {
        Text(
            "\u26a1 ${text.plain(text.highlights.title)}".uppercase(),
            fontSize = 12.sp,
            fontWeight = FontWeight.Bold,
            color = ReportColors.AmberDark,
            letterSpacing = 0.5.sp
        )
        if (lines.isEmpty()) {
            Text(text.plain(text.highlights.noGroundImpact), fontSize = 13.sp, color = ReportColors.Text, lineHeight = 19.sp)
        }
        lines.forEach { (label, value) ->
            Text(
                buildAnnotatedString {
                    withStyle(SpanStyle(fontWeight = FontWeight.Bold)) { append("${text.plain(label)}${text.labelColon()} ") }
                    append(value)
                },
                fontSize = 13.sp,
                color = ReportColors.Text,
                lineHeight = 19.sp
            )
        }
    }
}

internal data class Kpi(val value: String, val label: String, val color: Color)

// 4 tiles on one row on a wide screen, 2 x 2 on a phone
@Composable
internal fun KpiGrid(kpis: List<Kpi>) {
    BoxWithConstraints(Modifier.fillMaxWidth()) {
        val perRow = if (maxWidth < 560.dp) 2 else 4
        Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
            kpis.chunked(perRow).forEach { row ->
                Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    row.forEach { kpi ->
                        Column(
                            Modifier.weight(1f)
                                .clip(RoundedCornerShape(10.dp))
                                .background(ReportColors.Surface)
                                .border(1.dp, ReportColors.Border, RoundedCornerShape(10.dp))
                                .padding(vertical = 12.dp, horizontal = 6.dp),
                            horizontalAlignment = Alignment.CenterHorizontally,
                            verticalArrangement = Arrangement.spacedBy(4.dp)
                        ) {
                            Text(kpi.value, fontSize = 22.sp, fontWeight = FontWeight.ExtraBold, color = kpi.color)
                            Text(
                                kpi.label.uppercase(),
                                fontSize = 10.sp,
                                color = ReportColors.Muted,
                                textAlign = TextAlign.Center,
                                lineHeight = 13.sp
                            )
                        }
                    }
                }
            }
        }
    }
}

// ─── Page 2 — overview: notable impacts, Nsg comparison, detection network ──

@Composable
internal fun KeraunicOverviewSection(data: KeraunicReportData, text: KeraunicReportText) {
    val useFallback = data.topImpacts.isEmpty() && data.fallbackImpacts.isNotEmpty()
    ReportCard {
        ReportPageHeader(text.plain(text.overview.header))
        ReportSectionTitle("\uD83C\uDFAF ${text.plain(text.overview.notableImpacts)}")
        ReportBody(text.plain(if (useFallback) text.overview.fallbackSubtitle else text.overview.topSubtitle))
        when {
            useFallback -> data.fallbackImpacts.forEach { FallbackImpactRow(it, text) }
            data.topImpacts.isEmpty() -> Text(
                text.plain(text.overview.missingTopImpacts),
                modifier = Modifier.fillMaxWidth().padding(12.dp),
                fontSize = 13.sp,
                color = ReportColors.Light,
                textAlign = TextAlign.Center
            )
            else -> data.shownTopImpacts.forEachIndexed { i, impact -> TopImpactRow(i + 1, impact, text) }
        }

        ReportSectionTitle("\uD83D\uDCCA ${text.plain(text.overview.analysis)}")
        if (data.nsgRefs.isNotEmpty()) {
            Text(
                "\uD83D\uDCE1 ${text.plain(text.overview.nearestCities)}",
                fontSize = 13.sp,
                fontWeight = FontWeight.SemiBold,
                color = ReportColors.Blue
            )
            NsgComparison(data, text)
        }

        ReportSectionTitle("\uD83D\uDCE1 ${text.plain(text.overview.detectionNetwork)}")
        Box(Modifier.fillMaxWidth().clip(RoundedCornerShape(10.dp)).background(ReportColors.NetworkBg).padding(12.dp)) {
            ReportBody(text.plain(text.overview.detectionDescription), Color(0xFF3A4B6A))
        }
    }
}

@Composable
private fun ImpactRowBox(content: @Composable RowScope.() -> Unit) {
    Row(
        Modifier.fillMaxWidth()
            .clip(RoundedCornerShape(8.dp))
            .background(ReportColors.SurfaceAlt)
            .border(1.dp, ReportColors.Border, RoundedCornerShape(8.dp))
            .padding(horizontal = 12.dp, vertical = 8.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(10.dp)
    ) { content() }
}

@Composable
private fun TopImpactRow(rank: Int, impact: KeraunicTopImpact, text: KeraunicReportText) {
    ImpactRowBox {
        Text("$rank", fontSize = 18.sp, fontWeight = FontWeight.ExtraBold, color = ReportColors.Light, modifier = Modifier.width(22.dp))
        Column(Modifier.weight(1f)) {
            Text(
                "${impact.amplitudeKa.fmt(0)} kA ${polarityLabel(impact.isPositive)}",
                fontSize = 16.sp,
                fontWeight = FontWeight.Bold,
                color = polarityColor(impact.isPositive)
            )
            Text(
                "${text.fallbackImpactLabel(impact.externalId)} · ${text.impactDate(impact)} · ${impact.distKm.fmt(2)} km",
                fontSize = 11.sp,
                color = ReportColors.Muted
            )
        }
        if (KeraunicReportLogic.hasPercent(impact)) {
            Text("${impact.percent.fmt(0)} %", fontSize = 15.sp, fontWeight = FontWeight.Bold, color = ReportColors.Amber)
        }
    }
}

@Composable
private fun FallbackImpactRow(impact: KeraunicTopImpact, text: KeraunicReportText) {
    ImpactRowBox {
        Column(Modifier.weight(1f)) {
            Text(
                "${impact.amplitudeKa.fmt(1)} kA ${polarityLabel(impact.isPositive)}",
                fontSize = 16.sp,
                fontWeight = FontWeight.Bold,
                color = polarityColor(impact.isPositive)
            )
            Text(
                "${text.fallbackImpactLabel(impact.externalId)} · ${impact.distKm.fmt(2)} km",
                fontSize = 11.sp,
                color = ReportColors.Muted
            )
        }
    }
}

@Composable
private fun NsgComparison(data: KeraunicReportData, text: KeraunicReportText) {
    val maxNsg = (maxOf(data.nsg, data.nsgRefs.maxOf { it.value }) * 1.2).takeIf { it > 0.0 } ?: 1.0
    Column(
        Modifier.fillMaxWidth()
            .clip(RoundedCornerShape(10.dp))
            .background(ReportColors.SurfaceAlt)
            .border(1.dp, ReportColors.Border, RoundedCornerShape(10.dp))
            .padding(12.dp),
        verticalArrangement = Arrangement.spacedBy(5.dp)
    ) {
        NsgBar(text.plain(text.overview.yourSite), data.nsg, data.nsg.fmt(2), maxNsg, highlight = true)
        data.nsgRefs.forEach { ref ->
            NsgBar(KeraunicReportLogic.localizeCity(ref.label, text.lang), ref.value, ref.value.fmt(1), maxNsg, highlight = false)
        }
    }
}

@Composable
private fun NsgBar(label: String, value: Double, valueLabel: String, maxNsg: Double, highlight: Boolean) {
    val fraction = (value / maxNsg).toFloat().coerceIn(0.02f, 1f)
    Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
        Text(
            label,
            modifier = Modifier.widthIn(min = 84.dp, max = 110.dp),
            fontSize = if (highlight) 12.sp else 11.sp,
            fontWeight = if (highlight) FontWeight.Bold else FontWeight.Normal,
            color = if (highlight) ReportColors.Blue else ReportColors.Muted,
            textAlign = TextAlign.End,
            maxLines = 1
        )
        Box(
            Modifier.weight(1f).height(if (highlight) 20.dp else 16.dp)
                .clip(RoundedCornerShape(4.dp))
                .background(ReportColors.BarTrack)
        ) {
            Box(
                Modifier.fillMaxHeight().fillMaxWidth(fraction)
                    .clip(RoundedCornerShape(4.dp))
                    .background(if (highlight) ReportColors.Blue else ReportColors.CityBar),
                contentAlignment = Alignment.CenterEnd
            ) {
                Text(
                    valueLabel,
                    modifier = Modifier.padding(end = 5.dp),
                    fontSize = 10.sp,
                    fontWeight = FontWeight.Bold,
                    color = if (highlight) Color.White else Color(0xFF4A5A7A),
                    maxLines = 1
                )
            }
        }
    }
}

// ─── Page 3 — counts under the impact map ───────────────────────────────────

@Composable
internal fun KeraunicMapCounts(data: KeraunicReportData, text: KeraunicReportText) {
    Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(10.dp)) {
        MapCount(
            Modifier.weight(1f), data.zone1.totalCount, text.plain(text.map.directCount), text.plain(text.map.directDanger),
            ReportColors.Red, ReportColors.DirectBg
        )
        MapCount(
            Modifier.weight(1f), data.zone2.totalCount, text.plain(text.map.indirectCount), text.plain(text.map.indirectDanger),
            ReportColors.Amber, ReportColors.IndirectBg
        )
    }
}

@Composable
private fun MapCount(modifier: Modifier, count: Int, label: String, danger: String, color: Color, background: Color) {
    Column(
        modifier.clip(RoundedCornerShape(10.dp)).background(background)
            .border(1.5.dp, color, RoundedCornerShape(10.dp)).padding(10.dp),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        Text("$count", fontSize = 24.sp, fontWeight = FontWeight.ExtraBold, color = color)
        Text(label, fontSize = 12.sp, color = ReportColors.Muted, textAlign = TextAlign.Center)
        Text(danger, fontSize = 10.sp, color = ReportColors.Light, textAlign = TextAlign.Center)
    }
}

// ─── Pages 4-5 — zone analysis ─────────────────────────────────────────────

@Composable
internal fun KeraunicZoneSection(
    stats: KeraunicZoneStats,
    isDirect: Boolean,
    keyPoints: List<KeraunicTopImpact>,
    text: KeraunicReportText,
) {
    val accent = if (isDirect) ReportColors.Red else ReportColors.Amber
    val zoneLabel = if (isDirect) "0\u20131" else "1\u20132"
    val emoji = if (isDirect) "\u26a1" else "\ud83c\udf29"
    ReportCard {
        ReportPageHeader(text.plain(if (isDirect) text.zone.directSubtitle else text.zone.indirectSubtitle))
        ReportSectionTitle(
            "$emoji ${text.charts.zone} $zoneLabel km — " +
                "${text.plain(if (isDirect) text.zone.zoneDirect else text.zone.zoneIndirect)} (IEC 62305-2:2024)"
        )
        Column(
            Modifier.fillMaxWidth()
                .clip(RoundedCornerShape(12.dp))
                .background(if (isDirect) ReportColors.DirectBg else ReportColors.IndirectBg)
                .border(2.dp, accent, RoundedCornerShape(12.dp))
                .padding(14.dp),
            verticalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            Box(Modifier.clip(RoundedCornerShape(4.dp)).background(accent).padding(horizontal = 10.dp, vertical = 3.dp)) {
                Text(
                    "\u26a0 ${text.plain(if (isDirect) text.zone.directDanger else text.zone.indirectDanger)}",
                    fontSize = 11.sp,
                    fontWeight = FontWeight.Bold,
                    color = Color.White
                )
            }
            Text(
                "${stats.totalCount} ${text.plain(text.zone.recordedImpacts)}",
                fontSize = 19.sp,
                fontWeight = FontWeight.ExtraBold,
                color = if (isDirect) ReportColors.Red else ReportColors.AmberDark
            )
            ReportBody(text.plain(if (isDirect) text.zone.directDescription else text.zone.indirectDescription))
            val hasPolarity = KeraunicReportLogic.hasPolarity(stats)
            if (hasPolarity && stats.totalCount > 0) PolarityBar(stats, text)
            when {
                stats.totalCount == 0 -> Text(
                    text.plain(text.zone.noImpactsInZone),
                    modifier = Modifier.fillMaxWidth(),
                    fontSize = 13.sp,
                    color = ReportColors.Light,
                    textAlign = TextAlign.Center
                )
                hasPolarity -> ZoneStatsColumns(stats, text)
            }
        }
        if (isDirect && keyPoints.isNotEmpty()) KeyPoints(keyPoints, text)
    }
}

@Composable
private fun PolarityBar(stats: KeraunicZoneStats, text: KeraunicReportText) {
    val (negPct, posPct) = KeraunicReportLogic.polarityPercents(stats.negCount, stats.totalCount)
    Column(Modifier.fillMaxWidth(), horizontalAlignment = Alignment.CenterHorizontally, verticalArrangement = Arrangement.spacedBy(4.dp)) {
        Text(
            text.plain(text.zone.polarityDistribution).uppercase(),
            fontSize = 12.sp,
            fontWeight = FontWeight.SemiBold,
            color = ReportColors.Muted
        )
        Row(Modifier.fillMaxWidth().height(20.dp).clip(RoundedCornerShape(10.dp))) {
            if (negPct > 0) Box(Modifier.weight(negPct.toFloat()).fillMaxHeight().background(ReportColors.Blue))
            if (posPct > 0) Box(Modifier.weight(posPct.toFloat()).fillMaxHeight().background(ReportColors.Red))
        }
        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
            Text("■ CG\u2212 ${stats.negCount} ($negPct%)", fontSize = 13.sp, fontWeight = FontWeight.SemiBold, color = ReportColors.Blue)
            Text("CG+ ${stats.posCount} ($posPct%) ■", fontSize = 13.sp, fontWeight = FontWeight.SemiBold, color = ReportColors.Red)
        }
    }
}

@Composable
private fun ZoneStatsColumns(stats: KeraunicZoneStats, text: KeraunicReportText) {
    Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(12.dp)) {
        StatsGroup(
            Modifier.weight(1f), "\u26a1 ${text.plain(text.zone.negativeLightning)}", ReportColors.Blue,
            stats.negCount, stats.negMaxKa, stats.negAvgKa, stats.negMedianKa, text
        )
        StatsGroup(
            Modifier.weight(1f), "\u26a1 ${text.plain(text.zone.positiveLightning)}", ReportColors.Red,
            stats.posCount, stats.posMaxKa, stats.posAvgKa, stats.posMedianKa, text
        )
    }
}

@Suppress("LongParameterList")
@Composable
private fun StatsGroup(
    modifier: Modifier,
    title: String,
    color: Color,
    count: Int,
    maxKa: Double,
    avgKa: Double,
    medianKa: Double,
    text: KeraunicReportText,
) {
    Column(modifier, verticalArrangement = Arrangement.spacedBy(4.dp)) {
        Text(title, fontSize = 13.sp, fontWeight = FontWeight.Bold, color = color)
        StatLine(text.plain(text.zone.impactCount), "$count", color)
        StatLine(text.plain(text.zone.maximumIntensity), "${maxKa.fmt(0)} kA", color)
        StatLine(text.plain(text.zone.averageIntensity), "${avgKa.fmt(0)} kA", color)
        StatLine(text.plain(text.zone.median), "${medianKa.fmt(0)} kA", color)
    }
}

@Composable
private fun StatLine(label: String, value: String, color: Color) {
    Row(Modifier.fillMaxWidth().padding(vertical = 2.dp), horizontalArrangement = Arrangement.SpaceBetween) {
        Text(label, fontSize = 12.sp, color = ReportColors.Body, modifier = Modifier.weight(1f))
        Text(value, fontSize = 13.sp, fontWeight = FontWeight.Bold, color = color)
    }
}

@Composable
private fun KeyPoints(direct: List<KeraunicTopImpact>, text: KeraunicReportText) {
    val maxImpact = direct.maxByOrNull { it.amplitudeKa } ?: return
    val maxPos = direct.filter { it.isPositive }.maxByOrNull { it.amplitudeKa }
    Column(
        Modifier.fillMaxWidth()
            .clip(RoundedCornerShape(10.dp))
            .background(ReportColors.KeyPointsBg)
            .border(1.5.dp, ReportColors.Amber, RoundedCornerShape(10.dp))
            .padding(12.dp),
        verticalArrangement = Arrangement.spacedBy(8.dp)
    ) {
        Text(
            "\uD83D\uDCCC ${text.plain(text.zone.keyPoints)}".uppercase(),
            fontSize = 13.sp,
            fontWeight = FontWeight.Bold,
            color = ReportColors.Orange
        )
        KeyPoint(
            text.plain(text.zone.mostIntenseImpact), maxImpact, ReportColors.Blue,
            text.fill(text.plain(text.zone.distanceFromSite), maxImpact.distKm.fmt(2)), text
        )
        if (maxPos != null) {
            KeyPoint(text.plain(text.zone.strongestPositiveImpact), maxPos, ReportColors.Red, text.plain(text.zone.moreDestructive), text)
        }
    }
}

@Composable
private fun KeyPoint(title: String, impact: KeraunicTopImpact, color: Color, note: String, text: KeraunicReportText) {
    Column(
        Modifier.fillMaxWidth().clip(RoundedCornerShape(8.dp)).background(Color.White).padding(10.dp),
        verticalArrangement = Arrangement.spacedBy(2.dp)
    ) {
        Text(title, fontSize = 13.sp, fontWeight = FontWeight.Bold, color = ReportColors.Text)
        Text("${impact.amplitudeKa.fmt(0)} kA ${polarityLabel(impact.isPositive)}", fontSize = 18.sp, fontWeight = FontWeight.Bold, color = color)
        Text("${text.fallbackImpactLabel(impact.externalId)} · ${text.impactDate(impact)}", fontSize = 12.sp, color = ReportColors.Muted)
        Text(note, fontSize = 12.sp, color = ReportColors.Muted)
    }
}
